import { ReactNode } from 'react';
import {
  ConnectedEventInfo,
  ConnectedEventInfoProps,
} from 'src/components/Events/ConnectedEventInfo';
import { BoostListingButton } from 'src/components/SponsoredListings/BoostListingButton';
import { CampaignTabBar } from 'src/components/SponsoredListings/CampaignTabBar';
import {
  MultiSelectScope,
  useMultiSelectionContext,
} from 'src/contexts/MultiSelectionContext';
import { Stack } from 'src/core/ui';
import { useEnableSelectionByDefault } from 'src/hooks/useEnableSelectionByDefault';
import { useMatchMedia } from 'src/hooks/useMatchMedia';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { EventMappingStateFilter } from 'src/utils/eventQueryUtils';
import { Event, Feature, Performer, Tag, Venue } from 'src/WebApiController';

import { EventCheckbox } from '../EventListing/EventVirtualizedList/EventCheckbox';
import * as styles from './EventItemHeader.css';

export const METRICS_PLACEHOLDER_DIMS = { width: '50px' };

export type EventItemHeaderType = {
  event: Event;
  entityCount: number;
  performer?: Performer | null;
  venue?: Venue | null;
  hideYearFromDate: boolean;
  mainMetrics?: string | React.ReactNode;
  subMetrics?: string[] | React.ReactNode[];
  eventTags?: Tag[];
};

export type EventItemHeaderComponentType = Pick<
  ConnectedEventInfoProps,
  'showYear' | 'showMappingState' | 'showEventStatus' | 'eventTags'
> & {
  event: Event;
  performer?: Performer | null;
  venue?: Venue | null;
  actions?: ReactNode;
  headerMetrics?: ReactNode;
  metrics?: ReactNode;
  eventNameLink?: string;
  showBoost?: boolean;
};

/**
 * Wraps `ConnectedEventInfo` and adds actions and metrics.
 * @param props
 * @returns
 */
export const EventItemHeaderV2 = ({
  showYear,
  event,
  performer,
  venue,
  actions,
  headerMetrics,
  metrics,
  showMappingState,
  eventNameLink,
  showEventStatus,
  eventTags,
  showBoost,
}: EventItemHeaderComponentType) => {
  const isMobile = useMatchMedia('mobile');
  const { selectionMode } = useMultiSelectionContext();
  const enableSelectionByDefault = useEnableSelectionByDefault();

  const eventMappingState =
    event.viagId == null
      ? event.mapRejRsnId != null
        ? EventMappingStateFilter.Rejected
        : EventMappingStateFilter.Pending
      : undefined; // Mapped is not used for display yet

  const hasSponsoredListingsV2Feature = useUserHasFeature(
    Feature.SponsoredListingsV2
  );

  const hasMultiCampaignsFeature = useUserHasFeature(
    Feature.SponsoredListingsV2_MultiCampaignsSupport
  );

  const afterContent = hasSponsoredListingsV2Feature ? (
    <Stack className={styles.actions} gap="l" alignItems="center">
      <Stack gap="m" alignItems="center" width="max">
        {metrics} <div className={styles.dividerLine} /> {headerMetrics}
      </Stack>
      {showBoost && !hasMultiCampaignsFeature && (
        <BoostListingButton event={event} backgroundIsPrimary />
      )}
      <Stack gap="m" alignItems="center" width="max">
        {actions}
      </Stack>
    </Stack>
  ) : (
    <>
      <Stack className={styles.actions} gap="m" alignItems="center">
        {actions}
        {headerMetrics}
      </Stack>
      <Stack gap="s" alignItems="center">
        {metrics}
      </Stack>
    </>
  );

  return (
    <Stack
      className={styles.root}
      direction="column"
      gap="m"
      justifyContent="spaceBetween"
    >
      <div className={styles.headerRow}>
        {selectionMode?.mode === MultiSelectScope.AllGroups ? (
          <div className={styles.checkbox}>
            <EventCheckbox eventId={event?.virtId} />
          </div>
        ) : enableSelectionByDefault ? (
          // To keep the width of the event item consistent whether selectionMode is enabled or not,
          // we render an invisible placeholder when selectionMode is not enabled.
          <div className={styles.checkboxInvisiblePlaceholder}>
            <EventCheckbox eventId={event?.virtId} disabled />
          </div>
        ) : null}
        <ConnectedEventInfo
          showYear={showYear}
          showEventDateTbd
          event={event}
          performer={performer}
          venue={venue}
          showMappingState={showMappingState}
          eventMappingState={eventMappingState}
          mappingRejectionReasonId={event.mapRejRsnId}
          mappingRejectionReasonDescriptionDefault={event.mapRejDftTxt}
          eventNameLink={eventNameLink}
          showEventStatus={showEventStatus}
          eventTags={eventTags}
        />
        {isMobile ? (
          afterContent
        ) : (
          <Stack
            direction="column"
            gap="m"
            alignItems="end"
            style={{ alignSelf: 'center' }}
          >
            {afterContent}
          </Stack>
        )}
      </div>
      {hasSponsoredListingsV2Feature &&
        hasMultiCampaignsFeature &&
        showBoost && <CampaignTabBar event={event} />}
    </Stack>
  );
};
